// color-utils.js
// Utility functions for color handling in the analytics dashboard

/**
 * Gets a color based on a score percentage (0-100)
 * @param {number} score - Score from 0-100
 * @returns {string} Hex color code
 */
export const getColorFromPercentage = (score) => {
    if (score <= 65) {
        return '#ef4444'; // red
    } else if (score <= 80) {
        return '#f97316'; // orange
    } else {
        return '#22c55e'; // green
    }
};

/**
 * Gets a color gradient based on a score percentage (0-100)
 * @param {number} score - Score from 0-100
 * @returns {object} Object with main color and gradient
 */
export const getScoreColor = (score) => {
    if (score <= 65) {
        return {
            main: '#ef4444',
            gradient: ['#ef4444', '#f97316'], // red -> orange
        };
    } else if (score <= 80) {
        return {
            main: '#eab308',
            gradient: ['#f97316', '#84cc16'], // orange -> lime
        };
    } else {
        return {
            main: '#22c55e',
            gradient: ['#84cc16', '#22c55e'], // lime -> green
        };
    }
};

/**
 * Checks if a string is a valid CSS color
 * @param {string} color - The color string to validate
 * @returns {boolean} True if valid CSS color
 */
export const isValidCssColor = (color) => {
    if (!color) return false;

    // Create a temporary element to test the color
    const tempElem = document.createElement('div');
    tempElem.style.color = color;

    return tempElem.style.color !== '';
};

/**
 * Calculate the maximum value for a field across all records
 * @param {Array} records - All records
 * @param {string} field - Field name
 * @returns {number} Maximum value
 */
export const calculateMaxValue = (records, field) => {
    if (!records || !records.length || !field) return 100;

    const values = records
        .map(record => {
            const val = record[field];
            return typeof val === 'number' ? val : parseFloat(val);
        })
        .filter(val => !isNaN(val));

    if (!values.length) return 100;
    return Math.max(...values);
};

/**
 * Evaluates an expression within a safe context
 * @param {string} expression - Expression to evaluate
 * @param {Object} record - The record to provide as context
 * @param {*} defaultValue - Default value if evaluation fails
 * @returns {*} Result of evaluation
 */
export const evaluateExpression = (expression, record, defaultValue) => {
    if (!expression) return defaultValue;

    try {
        const fn = new Function('record', `return ${expression}`);
        return fn(record);
    } catch (error) {
        console.error('Error evaluating expression:', error, expression);
        return defaultValue;
    }
};


/**
 * Gets a color gradient based on a score percentage (0-100) with 10 gradient steps
 * @param {number} score - Score from 0-100
 * @returns {object} Object with main color and gradient
 */
export const getScoreColorGradient = (score) => {
    // Ensure score is within 0-100 range
    const safeScore = Math.min(100, Math.max(0, score));

    // Define 10 color steps and their gradients
    const colorData = [
        { main: '#ff0000', gradient: ['#ff0000', '#ff3300'] }, // 0-10%
        { main: '#ff3300', gradient: ['#ff3300', '#ff6600'] }, // 10-20%
        { main: '#ff6600', gradient: ['#ff6600', '#ff9900'] }, // 20-30%
        { main: '#ff9900', gradient: ['#ff9900', '#ffcc00'] }, // 30-40%
        { main: '#ffcc00', gradient: ['#ffcc00', '#ffee00'] }, // 40-50%
        { main: '#ffee00', gradient: ['#ffee00', '#ccff00'] }, // 50-60%
        { main: '#ccff00', gradient: ['#ccff00', '#99ff00'] }, // 60-70%
        { main: '#99ff00', gradient: ['#99ff00', '#66ff00'] }, // 70-80%
        { main: '#66ff00', gradient: ['#66ff00', '#00cc00'] }, // 80-90%
        { main: '#00cc00', gradient: ['#66ff00', '#00cc00'] }  // 90-100%
    ];

    // Calculate which step the score falls into (0-9)
    const stepIndex = Math.min(9, Math.floor(safeScore / 10));

    return colorData[stepIndex];
};

/**
 * Interpolates between two hex colors based on a percentage
 * For even smoother gradients when needed
 * @param {string} color1 - Starting hex color
 * @param {string} color2 - Ending hex color
 * @param {number} percent - Percentage between the two colors (0-100)
 * @returns {string} Interpolated hex color
 */
export const interpolateColor = (color1, color2, percent) => {
    // Add default fallback colors if inputs are invalid
    if (!color1 || typeof color1 !== 'string') color1 = '#ff0000'; // Default to red
    if (!color2 || typeof color2 !== 'string') color2 = '#00cc00'; // Default to green

    // Convert hex to RGB with safety checks
    const parseHex = (hexColor) => {
        try {
            // Ensure we have a string that starts with #
            const hex = hexColor.startsWith('#') ? hexColor.substring(1) : hexColor;

            // Handle different hex formats
            let r, g, b;
            if (hex.length === 3) {
                // Short hex format #RGB
                r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
                g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
                b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
            } else if (hex.length === 6) {
                // Standard hex format #RRGGBB
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
            } else {
                // Invalid format, use fallback
                console.warn('Invalid hex color format:', hexColor);
                return { r: 0, g: 0, b: 0 };
            }

            // Check for NaN and provide fallbacks
            return {
                r: isNaN(r) ? 0 : r,
                g: isNaN(g) ? 0 : g,
                b: isNaN(b) ? 0 : b
            };
        } catch (error) {
            console.error('Error parsing hex color:', error, hexColor);
            return { r: 0, g: 0, b: 0 };
        }
    };

    // Convert RGB to hex with safety checks
    const rgbToHex = (r, g, b) => {
        try {
            // Ensure values are valid numbers and in range
            r = Math.max(0, Math.min(255, Math.round(r || 0)));
            g = Math.max(0, Math.min(255, Math.round(g || 0)));
            b = Math.max(0, Math.min(255, Math.round(b || 0)));

            // Convert to hex with padding
            return '#' +
                r.toString(16).padStart(2, '0') +
                g.toString(16).padStart(2, '0') +
                b.toString(16).padStart(2, '0');
        } catch (error) {
            console.error('Error converting RGB to hex:', error);
            return '#000000'; // Default to black on error
        }
    };

    // Normalize percent to 0-1 with safety check
    const factor = Math.max(0, Math.min(1, (percent || 0) / 100));

    try {
        // Parse colors
        const c1 = parseHex(color1);
        const c2 = parseHex(color2);

        // Interpolate RGB values
        const r = c1.r + factor * (c2.r - c1.r);
        const g = c1.g + factor * (c2.g - c1.g);
        const b = c1.b + factor * (c2.b - c1.b);

        return rgbToHex(r, g, b);
    } catch (error) {
        console.error('Error in color interpolation:', error);
        return '#808080'; // Default to gray on error
    }
};

/**
 * Gets a color for a score with completely smooth gradation from red to green
 * @param {number} score - Score from 0-100
 * @returns {string} Hex color code
 */
export const getSmoothQualityColor = (score) => {
    try {
        // Ensure score is a valid number and within 0-100 range
        const safeScore = !score && score !== 0
            ? 50 // Default to middle value if score is undefined/null
            : Math.min(100, Math.max(0, Number(score) || 0));

        // Define color steps with valid hex colors
        const colorSteps = [
            '#ff0000', // 0%: Red
            '#ff9900', // 25%: Orange
            '#ffcc00', // 50%: Yellow
            '#99ff00', // 75%: Light green
            '#00cc00'  // 100%: Green
        ];

        // Determine which segment the score falls into
        const segmentSize = 100 / (colorSteps.length - 1);
        const segment = Math.min(colorSteps.length - 2, Math.floor(safeScore / segmentSize));

        // Calculate how far into this segment (0-1)
        const segmentPercent = (safeScore - segment * segmentSize) / segmentSize * 100;

        // Interpolate between the segment's colors
        return interpolateColor(colorSteps[segment], colorSteps[segment + 1], segmentPercent);
    } catch (error) {
        console.error('Error in getSmoothQualityColor:', error, score);
        return '#808080'; // Default to gray on error
    }
};

/**
 * Gets a color based on a score percentage (0-100) with 10 gradient steps from red to green
 * @param {number} score - Score from 0-100
 * @returns {string} Hex color code
 */
export const getQualityColor = (score) => {
    try {
        // Ensure score is a valid number and within 0-100 range
        const safeScore = !score && score !== 0
            ? 50 // Default to middle value if score is undefined/null
            : Math.min(100, Math.max(0, Number(score) || 0));

        // Define 10 color steps from red to green
        const colorSteps = [
            '#ff0000', // 0-10%: Bright red
            '#ff3300', // 10-20%: Red-orange
            '#ff6600', // 20-30%: Dark orange
            '#ff9900', // 30-40%: Orange
            '#ffcc00', // 40-50%: Amber
            '#ffee00', // 50-60%: Yellow
            '#ccff00', // 60-70%: Yellow-green
            '#99ff00', // 70-80%: Lime green
            '#66ff00', // 80-90%: Light green
            '#00cc00'  // 90-100%: Green
        ];

        // Calculate which step the score falls into (0-9)
        const stepIndex = Math.min(9, Math.floor(safeScore / 10));

        return colorSteps[stepIndex];
    } catch (error) {
        console.error('Error in getQualityColor:', error, score);
        return '#808080'; // Default to gray on error
    }
};
